// apps/share-web/src/text-share/live-follow.ts
//
// Что страница слов делает с якорем: подключаться ли и какую песню открыть.
//
// Чистая функция и ничего больше — ни DOM, ни часов, ни клиента. Правило волны:
// «новая фича = новые модули + тонкая оркестрация»; в `words-view.ts` остаётся
// проводка, а решения проверяются там, где раскладки нет вовсе.
//
// ТРИ ПРАВИЛА, И ВСЕ — ОБЕЩАНИЯ ЧИТАТЕЛЮ.
//
//  1. Открытая страница подключается САМА. Спека: «от музыканта не требуется
//     действия». Он открыл ссылку до служения, положил телефон на стойку — и
//     когда лидер начинает вести, лист обязан встать на нужную песню без
//     единого касания.
//  2. Ручное отключение ЛИПКОЕ. Без этого кнопка отключения не работает вовсе:
//     следующий же якорь (а он приходит раз в 10 с) отменял бы решение
//     читателя, и «отключиться» означало бы «отключиться на девять секунд».
//     Обратно — только клик: явное решение снимается явным решением.
//  3. ПОДКЛЮЧЁН — ЗНАЧИТ СЛЕДУЕШЬ. Открытый лист не на песне лидера
//     возвращается к лидеру, а не ждёт его следующего хода. Правило прямо
//     отменяет прежнее моё решение (было: «ушёл смотреть вперёд — не
//     выдёргиваем», по образцу `handOff()` у ведения листа), и отменяет его
//     Сергей на живой приёмке: «если я подключён — полностью мне показывать и
//     стараться перекидывать обратно, даже если я по навигации куда-то ухожу».
//     Хочешь листать — отключись кнопкой, она ровно для этого и есть, и
//     отключение липкое (правило 2).
//  4. ИЗ ОБЩЕГО СПИСКА ВОЗВРАЩАЕМ ТОЖЕ — но через ПАУЗУ (`LIST_GRACE_MS`).
//     Вторая приёмка того же правила: «если я выхожу в общий список песен, то
//     почему-то мне не возвращают обратно к секции и песне нужной». Первая
//     редакция правила 3 отдавала список читателю до следующего ХОДА лидера, а
//     лидер на своей песне стоит минуты — то есть «до следующего хода» на слух
//     означало «навсегда». Пауза вместо этого: см. `LIST_GRACE_MS`.
//  5. ПРОТУХШИЙ ЯКОРЬ НЕ ЗАПИРАЕТ. Пока связи нет (`stale`), правило 3 молчит:
//     читатель, ушедший на другую песню или в список, там и остаётся. Первое
//     подключение при этом работает (правило 1 — страница, открытая в провал
//     wifi, встаёт на песню лидера), и лист, оставшийся на песне лидера, идёт по
//     расписанию как шёл. Приёмка 12.09 (B-260912-live-lost-connection-traps-
//     viewer): лидер закрыл приложение, якорь не снят, и десять минут — до
//     потолка модели — страница каждые полсекунды возвращала музыканта на
//     последнюю песню лидера с красным «нет связи». Ведущий ушёл, читатель
//     заперт. Вернулась связь (`live`) — правило 3 возвращает его как прежде.
//     Правило покрыто ДВАЖДЫ и намеренно: здесь — решением (тесты ниже), и на
//     уровне ЭКРАНА — `__tests__/live-view-degrades.test.ts`. Обещание «не
//     запирает» живёт в модели, а запереть читателя может вьюха поверх неё
//     (inert от слоя чтения, disabled на рядах, возврат из импульса), и
//     мутационный прогон 21.09 показал, что без второго файла такой замок
//     ставится молча: ряды, выключенные при `stale`, не красят ни одного из 206
//     тестов этой папки.

import type { LinkState } from '../live/live-model.js';

/**
 * Сколько читатель стоит в общем списке, прежде чем лист вернётся сам. 10 с.
 *
 * У крестика во время служения есть НАСТОЯЩАЯ работа: посмотреть, что дальше и
 * сколько песен осталось. Мгновенный возврат отнимает эту работу целиком —
 * полсекунды на чтение сетлиста это не чтение, — а возврат «никогда» (первая
 * редакция правила 3: только на ходе лидера) отнимает лист: лидер стоит на своей
 * песне минуты, и на слух это «меня не возвращают». Пауза оставляет и то и
 * другое: список читается, а забыть про лист нельзя, потому что лист вернётся
 * сам.
 *
 * Число отдельной экспортируемой константой намеренно: Сергей сказал, что может
 * захотеть другое, и менять его надо одной строкой. Каденции опроса — рядом, в
 * `live/live-model.ts` (`POLL_WAITING_MS`, `POLL_LIVE_MS`, `POLL_SLOW_MS`).
 *
 * ⚠️ Порог сравнивается с паузой, которую отсчитывает страница ОТ МОМЕНТА
 * ЗАКРЫТИЯ листа, а не от подключения: закрыл → 10 с → вернуло; закрыл снова →
 * снова 10 с. Импульс страницы приходит раз в 500 мс, так что возврат случается
 * в пределах полусекунды после порога.
 */
export const LIST_GRACE_MS = 10_000;

export interface LiveStepArgs {
  /** Состояние связи СЕЙЧАС, как его считает часовая модель. */
  state: LinkState;
  /** Песня лидера, если позицию есть откуда вывести. */
  ledSongIdx: number | null;
  connected: boolean;
  /** Читатель отключился рукой. Снимается только его же кликом. */
  manualOff: boolean;
  /**
   * Какая песня ОТКРЫТА прямо сейчас. −1 — слой закрыт, читатель в списке.
   *
   * Этим живёт правило 3: сравнивать надо с тем, что человек ВИДИТ, а не с
   * прошлым ходом лидера. Пока лист открыт на песне лидера — переходить некуда,
   * и ответ `null` здесь не роскошь, а условие годности: слой пересобирается
   * монтажом, а импульс страницы приходит раз в 500 мс, то есть «переоткрывать
   * ту же песню» означало бы пересборку листа дважды в секунду — конец и
   * прокрутке, и батарее.
   */
  readingIdx: number;
  /**
   * Песня, на которую страница уже переходила по воле лидера. −1 — ещё ни разу.
   *
   * Для открытого листа поле не нужно вовсе (там решает `readingIdx`); у
   * ЗАКРЫТОГО оно различает два возврата, и различие видно только внутри паузы:
   * ход лидера отменяет паузу немедленно, потому что читателю в списке после
   * смены песни решать уже нечего. Вне паузы поле не решает ничего — вернём и
   * так.
   */
  lastLedIdx: number;
  /**
   * Когда читатель ЗАКРЫЛ лист (часы страницы), или null — закрытия, которое
   * надо переждать, нет.
   *
   * `null` означает «возвращать не откладывая»: так выглядят и страница, ещё не
   * открывавшая лист, и читатель, который только что нажал кнопку Live обратно —
   * он попросил вести его, и заставлять его ждать десять секунд после явного
   * жеста было бы издевательством.
   */
  closedAtMs: number | null;
  /**
   * Локальное «сейчас». Швом, как часы в `live-model.ts`, и по той же причине:
   * утверждение «через 9 с не возвращаем, через 10 возвращаем» недоказуемо, если
   * эти секунды надо переждать. Своего `Date.now()` в модуле нет.
   */
  nowMs: number;
}

export interface LiveStep {
  /** Подключиться без клика — сессия появилась, а читатель не отказывался. */
  connect: boolean;
  /** Открыть эту песню. null — переходить некуда. */
  openAt: number | null;
}

export function liveStep(args: LiveStepArgs): LiveStep {
  // Сессия ЕСТЬ и при протухшем якоре: `stale` — утверждение про связь, а не
  // про лист (правило 3 модели). Страница, открытая посреди провала гостевого
  // wifi, обязана вести по расписанию так же, как открытая минутой раньше.
  const session = args.state === 'live' || args.state === 'stale';
  const connect = session && !args.connected && !args.manualOff;
  const following = args.connected || connect;
  return { connect, openAt: following ? openTarget(args) : null };
}

/** Куда переходить, когда мы следуем за лидером. null — уже там или некуда. */
function openTarget(args: LiveStepArgs): number | null {
  const led = args.ledSongIdx;
  // Позиции нет — вести нечем: лидер молчит дольше потолка, песня кончилась,
  // якоря ещё не было. Утаскивать читателя в этот момент некуда.
  if (led === null) return null;
  // ПРАВИЛО 5: связи нет — никого не возвращаем. Единственный переход в этом
  // состоянии — самый первый (страница ещё не вела никого, `lastLedIdx` −1).
  if (args.state === 'stale' && args.lastLedIdx !== -1) return null;
  // ЛИСТ ОТКРЫТ — правило 3, и никаких оговорок: не та песня → возвращаем.
  if (args.readingIdx >= 0) return args.readingIdx === led ? null : led;
  // ЛИСТ ЗАКРЫТ — читатель в общем списке.
  //
  // Ход лидера возвращает НЕМЕДЛЕННО, паузу не дожидаясь: лидер сменил песню, и
  // тому, кто читает список, решать больше нечего. Первое подключение — тоже
  // ход: `lastLedIdx` там ещё −1.
  if (led !== args.lastLedIdx) return led;
  // Лидер стоит на той же песне. Возврат всё равно будет — но не раньше, чем
  // список успеют прочитать (`LIST_GRACE_MS`).
  if (args.closedAtMs === null) return led;
  return args.nowMs - args.closedAtMs >= LIST_GRACE_MS ? led : null;
}
